import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { socket } from '../App';

// Fix icône leaflet par défaut (webpack)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface Session {
    id: number;
    device_ip: string;
    connected_at: number;
    disconnected_at: number | null;
    duration_ms: number | null;
}

interface NotifyConfig {
    telegram_token: string | null;
    telegram_chat_id: string | null;
    watched_ips: string[];
    notify_connect: boolean;
    notify_disconnect: boolean;
    idle_timeout_min: number;
}

const DEVICE_NAMES: Record<string, string> = {
    '192.168.1.11': '💻 PC',
    '192.168.1.14': '📺 LG TV',
    '192.168.1.20': '📱 iPhone',
};

function fmt(ts: number) {
    return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}
function fmtDate(ts: number) {
    return new Date(ts).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}
function fmtDuration(ms: number | null) {
    if (!ms) return '—';
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

// Construit les jours des N derniers jours
function buildDays(days: number): { label: string; date: Date; ts: number }[] {
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        d.setHours(0, 0, 0, 0);
        result.push({ label: fmtDate(d.getTime()), date: d, ts: d.getTime() });
    }
    return result;
}

// Positionne une session dans la journée (% de 0h à 24h)
function sessionToBar(session: Session, dayStart: number) {
    const dayEnd = dayStart + 86_400_000;
    const start = Math.max(session.connected_at, dayStart);
    const end = session.disconnected_at
        ? Math.min(session.disconnected_at, dayEnd)
        : Math.min(Date.now(), dayEnd);
    if (start >= dayEnd || end <= dayStart) return null;
    const left = ((start - dayStart) / 86_400_000) * 100;
    const width = ((end - start) / 86_400_000) * 100;
    return { left, width: Math.max(width, 0.3) };
}

export function SessionsView() {
    const [devices, setDevices] = useState<string[]>([]);
    const [selectedIp, setSelectedIp] = useState('');
    const [days, setDays] = useState(7);
    const [sessions, setSessions] = useState<Session[]>([]);
    const [status, setStatus] = useState<Record<string, { online: boolean; lastSeen: number }>>({});
    const [deviceNames, setDeviceNames] = useState<Record<string, string>>(DEVICE_NAMES);

    // Labels dynamiques depuis le serveur (ex: iPhone auto-détecté)
    useEffect(() => {
        if (!socket) return;
        const onLabel = ({ deviceIp, label }: { deviceIp: string; label: string }) => {
            setDeviceNames(prev => ({ ...prev, [deviceIp]: `📱 ${label}` }));
        };
        socket.on('device-label', onLabel);
        return () => { socket.off('device-label', onLabel); };
    }, []);

    const getLabel = (ip: string) => deviceNames[ip] || ip;

    const [notifyConfig, setNotifyConfig] = useState<NotifyConfig>({
        telegram_token: null, telegram_chat_id: null, watched_ips: [],
        notify_connect: true, notify_disconnect: true, idle_timeout_min: 5,
    });
    const [newToken, setNewToken] = useState('');
    const [newChatId, setNewChatId] = useState('');
    const [saveMsg, setSaveMsg] = useState('');
    const [testMsg, setTestMsg] = useState('');
    const [showConfig, setShowConfig] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [homeCoords, setHomeCoords] = useState<{ lat: number; lng: number } | null>(() => {
        try { const s = localStorage.getItem('home_coords'); return s ? JSON.parse(s) : null; } catch { return null; }
    });
    const [coordInput, setCoordInput] = useState(homeCoords ? `${homeCoords.lat},${homeCoords.lng}` : '');

    // Charger devices + status — combine DNS devices ET monitor (iPhone présent même sans DNS)
    const loadDevices = useCallback(async () => {
        const [devRes, statusRes] = await Promise.all([
            fetch('/api/dns/devices', { credentials: 'include' }),
            fetch('/api/monitor/status', { credentials: 'include' }),
        ]);
        const devs: any[] = await devRes.json();
        const stat: Record<string, { online: boolean; lastSeen: number }> = await statusRes.json();
        setStatus(stat);
        // Union des IPs DNS + IPs monitorées
        const ipSet = new Set<string>([
            ...devs.map((d: any) => d.device_ip),
            ...Object.keys(stat),
        ]);
        // Toujours inclure les IPs statiques connues qui ont des sessions
        Object.keys(DEVICE_NAMES).forEach(ip => ipSet.add(ip));
        const ips = Array.from(ipSet);
        setDevices(ips);
        if (!selectedIp && ips.length > 0) {
            // Préférer l'iPhone s'il est présent
            const iphone = ips.find(ip => DEVICE_NAMES[ip]?.includes('iPhone'));
            setSelectedIp(iphone || ips[0]);
        }
    }, [selectedIp]);

    // Charger les sessions pour l'IP sélectionnée
    const loadSessions = useCallback(async () => {
        if (!selectedIp) return;
        const res = await fetch(`/api/sessions/${encodeURIComponent(selectedIp)}?days=${days}`, { credentials: 'include' });
        setSessions(await res.json());
    }, [selectedIp, days]);

    // Charger config notifs
    const loadNotifyConfig = useCallback(async () => {
        const res = await fetch('/api/notify/config', { credentials: 'include' });
        const cfg = await res.json();
        setNotifyConfig(cfg);
    }, []);

    useEffect(() => { loadDevices(); loadNotifyConfig(); }, []);
    useEffect(() => { loadSessions(); }, [loadSessions]);

    // Mise à jour temps réel
    useEffect(() => {
        if (!socket) return;
        const onStatus = (d: { deviceIp: string; online: boolean; timestamp: number }) => {
            setStatus(prev => ({ ...prev, [d.deviceIp]: { online: d.online, lastSeen: d.timestamp } }));
            if (d.deviceIp === selectedIp) loadSessions();
        };
        socket.on('device-status', onStatus);
        return () => { socket.off('device-status', onStatus); };
    }, [selectedIp, loadSessions]);

    // Refresh auto toutes les 60s
    useEffect(() => {
        const t = setInterval(() => { loadSessions(); loadDevices(); }, 60_000);
        return () => clearInterval(t);
    }, [loadSessions, loadDevices]);

    const saveNotify = async () => {
        const body: any = {
            notify_connect: notifyConfig.notify_connect,
            notify_disconnect: notifyConfig.notify_disconnect,
            idle_timeout_min: notifyConfig.idle_timeout_min,
            watched_ips: notifyConfig.watched_ips,
        };
        if (newToken) body.telegram_token = newToken;
        if (newChatId) body.telegram_chat_id = newChatId;
        await fetch('/api/notify/config', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        setSaveMsg('✅ Sauvegardé');
        setNewToken(''); setNewChatId('');
        setTimeout(() => setSaveMsg(''), 3000);
        loadNotifyConfig();
    };

    const testNotif = async () => {
        const res = await fetch('/api/notify/test', { method: 'POST', credentials: 'include' });
        const d = await res.json();
        setTestMsg(d.ok ? '✅ Message envoyé !' : `❌ ${d.error}`);
        setTimeout(() => setTestMsg(''), 4000);
    };

    const dayList = buildDays(days);
    const isOnline = status[selectedIp]?.online ?? false;
    const lastSeen = status[selectedIp]?.lastSeen;
    const deviceLabel = getLabel(selectedIp);

    // Heures axe X (0h, 4h, 8h, …, 24h)
    const hourMarks = Array.from({ length: 7 }, (_, i) => i * 4);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Header sélecteur + statut */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e5e7eb' }} className="filter-bar">
                <span style={{ fontWeight: 700, fontSize: 16 }}>📶 Présence appareils</span>
                <select value={selectedIp} onChange={e => setSelectedIp(e.target.value)}
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#f9fafb' }}>
                    {devices.map(ip => (
                        <option key={ip} value={ip}>{getLabel(ip)}</option>
                    ))}
                </select>
                <select value={days} onChange={e => setDays(Number(e.target.value))}
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#f9fafb' }}>
                    <option value={3}>3 jours</option>
                    <option value={7}>7 jours</option>
                    <option value={14}>14 jours</option>
                    <option value={30}>30 jours</option>
                </select>

                {/* Statut actuel */}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 99,
                        background: isOnline ? '#dcfce7' : '#f3f4f6', border: `1px solid ${isOnline ? '#86efac' : '#e5e7eb'}` }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: isOnline ? '#22c55e' : '#94a3b8',
                            boxShadow: isOnline ? '0 0 0 3px #bbf7d0' : 'none',
                            animation: isOnline ? 'pulse 2s infinite' : 'none' }} />
                        <span style={{ fontWeight: 700, fontSize: 14, color: isOnline ? '#15803d' : '#64748b' }}>
                            {deviceLabel} — {isOnline ? 'EN LIGNE' : 'HORS LIGNE'}
                        </span>
                    </div>
                    {lastSeen && !isOnline && (
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>
                            vu {new Date(lastSeen).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    )}
                    <button onClick={() => setShowConfig(!showConfig)}
                        style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: showConfig ? '#f1f5f9' : '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                        🔔 Notifs
                    </button>
                    <button onClick={() => setShowMap(!showMap)}
                        style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: showMap ? '#f1f5f9' : '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                        🗺 Carte
                    </button>
                </div>
            </div>

            {/* Config notifications */}
            {showConfig && (
                <div style={{ background: '#1e293b', borderRadius: 12, padding: '20px 24px', border: '1px solid #334155' }}>
                    <h3 style={{ color: '#f8fafc', margin: '0 0 16px', fontSize: 15, fontWeight: 700 }}>
                        🤖 Notifications Telegram
                    </h3>
                    <div className="grid-2col" style={{ marginBottom: 16 }}>
                        <div>
                            <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>
                                Bot Token <span style={{ color: '#64748b' }}>(via @BotFather)</span>
                            </label>
                            <input value={newToken} onChange={e => setNewToken(e.target.value)}
                                placeholder={notifyConfig.telegram_token ? notifyConfig.telegram_token : 'Nouveau token...'}
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div>
                            <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>
                                Chat ID <span style={{ color: '#64748b' }}>(via @userinfobot)</span>
                            </label>
                            <input value={newChatId} onChange={e => setNewChatId(e.target.value)}
                                placeholder={notifyConfig.telegram_chat_id || 'Ton chat ID...'}
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
                        <label style={{ color: '#cbd5e1', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" checked={notifyConfig.notify_connect}
                                onChange={e => setNotifyConfig(p => ({ ...p, notify_connect: e.target.checked }))} />
                            🟢 Notif connexion
                        </label>
                        <label style={{ color: '#cbd5e1', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" checked={notifyConfig.notify_disconnect}
                                onChange={e => setNotifyConfig(p => ({ ...p, notify_disconnect: e.target.checked }))} />
                            ⚫ Notif déconnexion
                        </label>
                        <label style={{ color: '#cbd5e1', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                            ⏱ Seuil hors ligne :
                            <select value={notifyConfig.idle_timeout_min}
                                onChange={e => setNotifyConfig(p => ({ ...p, idle_timeout_min: Number(e.target.value) }))}
                                style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', fontSize: 12 }}>
                                {[2, 5, 10, 15, 30].map(v => <option key={v} value={v}>{v} min</option>)}
                            </select>
                        </label>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <button onClick={saveNotify}
                            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                            Sauvegarder
                        </button>
                        <button onClick={testNotif}
                            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #334155', background: 'transparent', color: '#94a3b8', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                            Envoyer test
                        </button>
                        {saveMsg && <span style={{ color: '#86efac', fontSize: 13 }}>{saveMsg}</span>}
                        {testMsg && <span style={{ color: '#86efac', fontSize: 13 }}>{testMsg}</span>}
                    </div>
                    <p style={{ color: '#475569', fontSize: 12, margin: '12px 0 0' }}>
                        1. Crée un bot via <b style={{ color: '#94a3b8' }}>@BotFather</b> → /newbot → copie le token<br />
                        2. Envoie un message à ton bot, puis va sur <b style={{ color: '#94a3b8' }}>@userinfobot</b> pour avoir ton Chat ID<br />
                        3. Colle token + ID ici, sauvegarde et teste
                    </p>
                </div>
            )}

            {/* Carte localisation */}
            {showMap && (
                <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#374151' }}>🗺 Localisation domicile</h3>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
                            <input
                                value={coordInput}
                                onChange={e => setCoordInput(e.target.value)}
                                placeholder="48.7266,2.4216 (lat,lng)"
                                style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12, fontFamily: 'monospace', width: 200 }}
                            />
                            <button onClick={() => {
                                const [lat, lng] = coordInput.split(',').map(Number);
                                if (!isNaN(lat) && !isNaN(lng)) {
                                    const c = { lat, lng };
                                    setHomeCoords(c);
                                    localStorage.setItem('home_coords', JSON.stringify(c));
                                }
                            }} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                                Sauver
                            </button>
                        </div>
                    </div>
                    {homeCoords ? (
                        <div style={{ height: 300, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                            <MapContainer center={[homeCoords.lat, homeCoords.lng]} zoom={14} style={{ height: '100%', width: '100%' }}>
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                <Marker position={[homeCoords.lat, homeCoords.lng]}>
                                    <Popup>
                                        <b>{deviceLabel}</b><br />
                                        {isOnline ? '🟢 En ligne' : '⚫ Hors ligne'}<br />
                                        {lastSeen && `Vu le ${new Date(lastSeen).toLocaleString('fr-FR')}`}
                                    </Popup>
                                </Marker>
                            </MapContainer>
                        </div>
                    ) : (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8 }}>
                            Entre les coordonnées de ton domicile (ex: <b>48.7266,2.4216</b> pour Draveil)<br />
                            <a href="https://www.google.fr/maps" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#6366f1' }}>
                                Chercher sur Google Maps → clic droit → "Quelles sont les coordonnées ?"
                            </a>
                        </div>
                    )}
                </div>
            )}

            {/* Calendrier de présence */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: '#374151' }}>
                    Présence — {deviceLabel}
                </h3>

                {/* Axe des heures */}
                <div style={{ display: 'flex', marginLeft: 80, marginBottom: 4 }}>
                    {hourMarks.map(h => (
                        <div key={h} style={{ flex: h === 20 ? 1 : 1, fontSize: 10, color: '#9ca3af', textAlign: 'center',
                            width: `${100 / 6}%`, flexShrink: 0 }}>
                            {String(h).padStart(2, '0')}h
                        </div>
                    ))}
                </div>

                {/* Lignes par jour */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {dayList.map(day => {
                        const daySessions = sessions.filter(s =>
                            s.connected_at < day.ts + 86_400_000 &&
                            (s.disconnected_at ?? Date.now()) > day.ts
                        );
                        const isToday = new Date().toDateString() === day.date.toDateString();

                        return (
                            <div key={day.ts} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 72, fontSize: 11, color: isToday ? '#6366f1' : '#6b7280',
                                    fontWeight: isToday ? 700 : 400, textAlign: 'right', flexShrink: 0 }}>
                                    {day.label}
                                </div>
                                <div style={{ flex: 1, height: 24, background: '#f8fafc', borderRadius: 6,
                                    border: `1px solid ${isToday ? '#e0e7ff' : '#e5e7eb'}`,
                                    position: 'relative', overflow: 'hidden' }}>
                                    {/* Lignes de quart de journée */}
                                    {[25, 50, 75].map(p => (
                                        <div key={p} style={{ position: 'absolute', left: `${p}%`, top: 0, bottom: 0,
                                            width: 1, background: '#e5e7eb' }} />
                                    ))}
                                    {/* Barres de session */}
                                    {daySessions.map(s => {
                                        const bar = sessionToBar(s, day.ts);
                                        if (!bar) return null;
                                        const isActive = !s.disconnected_at;
                                        return (
                                            <div key={s.id}
                                                title={`${fmt(s.connected_at)} → ${s.disconnected_at ? fmt(s.disconnected_at) : 'en cours'} (${fmtDuration(s.duration_ms)})`}
                                                style={{
                                                    position: 'absolute', top: 3, bottom: 3,
                                                    left: `${bar.left}%`, width: `${bar.width}%`,
                                                    borderRadius: 3,
                                                    background: isActive
                                                        ? 'linear-gradient(90deg, #22c55e, #4ade80)'
                                                        : 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                                                    opacity: 0.85,
                                                    cursor: 'default',
                                                    boxShadow: isActive ? '0 0 6px #22c55e66' : 'none',
                                                }} />
                                        );
                                    })}
                                    {daySessions.length === 0 && (
                                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                                            justifyContent: 'center', fontSize: 10, color: '#d1d5db' }}>
                                            aucune activité
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: '#9ca3af' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 12, height: 8, borderRadius: 2, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', display: 'inline-block' }} />
                        Session terminée
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 12, height: 8, borderRadius: 2, background: 'linear-gradient(90deg, #22c55e, #4ade80)', display: 'inline-block' }} />
                        En ligne actuellement
                    </span>
                    <span style={{ marginLeft: 'auto' }}>Survoler une barre pour les détails</span>
                </div>
            </div>

            {/* Tableau des sessions */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#374151' }}>
                    Historique des connexions ({sessions.length})
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#f9fafb' }}>
                                {['Jour', 'Connexion', 'Déconnexion', 'Durée', 'Statut'].map(h => (
                                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.slice(0, 100).map((s, i) => {
                                const active = !s.disconnected_at;
                                return (
                                    <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6', background: active ? '#f0fdf4' : (i % 2 === 0 ? '#fff' : '#fafafa') }}>
                                        <td style={{ padding: '8px 14px', color: '#374151', fontWeight: 500 }}>{fmtDate(s.connected_at)}</td>
                                        <td style={{ padding: '8px 14px', color: '#22c55e', fontWeight: 700, fontFamily: 'monospace' }}>🟢 {fmt(s.connected_at)}</td>
                                        <td style={{ padding: '8px 14px', color: active ? '#9ca3af' : '#6366f1', fontFamily: 'monospace' }}>
                                            {active ? '— en cours' : `⚫ ${fmt(s.disconnected_at!)}`}
                                        </td>
                                        <td style={{ padding: '8px 14px', color: '#6b7280' }}>{active ? '…' : fmtDuration(s.duration_ms)}</td>
                                        <td style={{ padding: '8px 14px' }}>
                                            <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                                                background: active ? '#dcfce7' : '#f1f5f9',
                                                color: active ? '#15803d' : '#64748b' }}>
                                                {active ? 'En ligne' : 'Terminée'}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                            {sessions.length === 0 && (
                                <tr>
                                    <td colSpan={5} style={{ padding: '32px', textAlign: 'center', color: '#9ca3af' }}>
                                        Aucune session — le daemon est-il lancé et l'appareil actif ?
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
